<template>
  <aside
    class="fixed inset-y-0 left-0 z-40 flex flex-col border-r border-white/10 bg-[#172438] text-white shadow-[0_8px_24px_rgba(15,23,42,0.14)] transition-[width] duration-200"
    :class="isCollapsed ? 'w-[72px]' : 'w-[260px]'"
  >
    <div class="relative flex h-20 shrink-0 items-center border-b border-[#31435d]" :class="isCollapsed ? 'justify-center px-0' : 'justify-between px-5'">
      <router-link to="/staff/surveys" class="overflow-hidden text-white">
        <span v-if="!isCollapsed" class="block text-[28px] font-medium leading-none tracking-[0.04em]">ARQAU</span>
        <svg v-else width="32" height="32" viewBox="0 0 32 32" fill="none"><path d="M5.3 16H13a3 3 0 013 3v7.7M26.7 16H19a3 3 0 01-3-3V5.3" stroke="white" stroke-width="1.5"/></svg>
      </router-link>
      <div v-if="!isCollapsed" class="flex rounded-full border border-white/10 bg-white/10 p-0.5 text-[10px] font-semibold">
        <button v-for="language in languages" :key="language.value" type="button" class="rounded-full px-2 py-1 transition" :class="lang === language.value ? 'bg-[#0066ff] text-white' : 'text-white/60 hover:text-white'" @click="switchLang(language.value)">{{ language.label }}</button>
      </div>
    </div>

    <button
      type="button"
      class="absolute -right-3.5 top-[91px] z-50 flex h-7 w-7 items-center justify-center rounded-full border border-[#495b76] bg-[#1b283b] text-white shadow-lg transition hover:scale-105 hover:bg-[#23354f]"
      :aria-label="isCollapsed ? t('Развернуть меню') : t('Свернуть меню')"
      @click="toggleSidebar"
    >
      <svg class="h-4 w-4 transition-transform" :class="{ 'rotate-180': isCollapsed }" viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8l4-4" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
    </button>

    <nav class="flex flex-1 flex-col gap-2 overflow-y-auto overflow-x-hidden py-6" :class="isCollapsed ? 'px-3' : 'px-4'">
      <router-link
        v-for="item in navItems"
        :key="item.to"
        :to="item.to"
        class="staff-nav-link"
        :class="[{ active: isActive(item.to) }, isCollapsed ? 'justify-center px-0' : '']"
        :title="isCollapsed ? item.label : undefined"
      >
        <svg class="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.4" :d="item.icon" /></svg>
        <span v-if="!isCollapsed" class="truncate">{{ item.label }}</span>
      </router-link>
    </nav>

    <div class="shrink-0 px-4 pb-5">
      <button v-if="isCollapsed" type="button" class="mb-2 flex h-10 w-full items-center justify-center rounded-[10px] text-[11px] font-semibold text-[#8799b0] transition hover:bg-white/10 hover:text-white" @click="switchLang(lang === 'ru' ? 'kz' : 'ru')">{{ lang === 'ru' ? 'ҚАЗ' : 'RU' }}</button>

      <div class="border-t border-white/10 pt-4">
        <div class="flex items-center rounded-2xl py-2 transition hover:bg-[#1b283b]" :class="isCollapsed ? 'justify-center' : 'gap-3 px-1'">
          <div class="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#23354f] text-sm font-semibold ring-2 ring-[#172438]">
            {{ userInitials }}
            <span class="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-[#172438] bg-[#34d399]"></span>
          </div>
          <div v-if="!isCollapsed" class="min-w-0 flex-1">
            <p class="truncate text-sm font-medium text-white">{{ userName }}</p>
            <p class="mt-0.5 truncate text-[11px] text-[#8799b0]">ARQAU · {{ t('Сотрудник') }}</p>
          </div>
        </div>
      </div>

      <button type="button" class="staff-nav-link mt-1 w-full hover:bg-red-500/10" :class="isCollapsed ? 'justify-center px-0' : ''" :title="isCollapsed ? t('Выйти') : undefined" @click="logout">
        <svg class="h-5 w-5 shrink-0" viewBox="0 0 20 20" fill="none"><path d="M2.1 10.4h10.8m0 0l-4.2 4.2m4.2-4.2L8.7 6.2M17.5 16.7V3.3" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
        <span v-if="!isCollapsed">{{ t('Выйти') }}</span>
      </button>
    </div>
  </aside>
</template>

<script>
import api from '@/services/api'
import { t, setLang, currentLang } from '@/i18n'

export default {
  name: 'StaffSidebar',
  data() {
    return {
      isCollapsed: localStorage.getItem('staffSidebarCollapsed') === 'true',
      currentUser: null,
      languages: [{ value: 'ru', label: 'RU' }, { value: 'kz', label: 'ҚАЗ' }]
    }
  },
  computed: {
    lang() { return currentLang.value },
    navItems() {
      return [
        { to: '/staff/regions', label: this.t('Регионы'), icon: 'M9 20l-5.4-2.7A1 1 0 013 16.4v-14l5.6 2.8a1 1 0 00.9 0l5.1-2.6a1 1 0 01.9 0L21 5.4v14.2a1 1 0 01-1.4.9l-5.2-2.5a1 1 0 00-.8 0l-3.2 1.5a1 1 0 01-.9 0z' },
        { to: '/staff/clinics', label: this.t('Клиники'), icon: 'M3 21h18M5 21V5a2 2 0 012-2h6a2 2 0 012 2v16m4 0V9a2 2 0 00-2-2h-2M9 7h2m-2 4h2m-2 4h2' },
        { to: '/staff/users', label: this.t('Пользователи'), icon: 'M17 20h5v-2a3 3 0 00-5.4-1.9M17 20H7m10 0v-2c0-.7-.1-1.3-.4-1.9M7 20H2v-2a3 3 0 015.4-1.9M7 20v-2c0-.7.1-1.3.4-1.9a5 5 0 019.2 0M15 7a3 3 0 11-6 0 3 3 0 016 0z' },
        { to: '/staff/surveys', label: this.t('Опросники'), icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 12h6m-6 4h6' },
        { to: '/staff/psychological-monitoring', label: this.t('Психомониторинг'), icon: 'M11 3.1A9 9 0 1020.9 13H11V3.1zM20.5 9H15V3.5A9 9 0 0120.5 9z' },
        { to: '/staff/mobile-content', label: this.t('Контент приложения'), icon: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z' }
      ]
    },
    userName() { return this.currentUser?.full_name || this.currentUser?.username || this.t('Сотрудник') },
    userInitials() { return this.userName.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase() || 'U' }
  },
  mounted() { this.applySidebarWidth(); this.loadUserData() },
  beforeUnmount() { document.documentElement.style.setProperty('--sidebar-width', '260px') },
  methods: {
    t,
    switchLang(lang) { setLang(lang) },
    toggleSidebar() { this.isCollapsed = !this.isCollapsed; localStorage.setItem('staffSidebarCollapsed', String(this.isCollapsed)); this.applySidebarWidth() },
    applySidebarWidth() { document.documentElement.style.setProperty('--sidebar-width', this.isCollapsed ? '72px' : '260px') },
    isActive(path) { return this.$route.path.startsWith(path) },
    async loadUserData() { try { const response = await api.checkAuth(); this.currentUser = response.data?.user || null } catch (_) { this.currentUser = null } },
    async logout() { try { await api.logout() } finally { this.$router.push('/login') } }
  }
}
</script>

<style scoped>
.staff-nav-link { @apply flex items-center gap-4 rounded-[10px] px-[10px] py-[10px] text-[15px] font-normal text-[#8799b0] transition-colors hover:bg-[#1b283b] hover:text-white; }
.staff-nav-link.active { @apply bg-[#0066ff] text-white; }
</style>
